/**
 * Settings → Email inbox: the three addresses of the business and who may send to them.
 *
 * Members and the business's own address are always allowed and shown as such, so the owner sees
 * the whole policy in one place; the editable list holds suppliers and billing platforms that send
 * straight to the inbox.
 */
import { Copy, Plus, RefreshCw, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { KindBadge } from "@/components/documents/document-badges";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useRotateInboxToken, useUpdateAllowlist } from "@/hooks/use-inbox";
import { useMembers } from "@/hooks/use-workspaces";
import { INBOX_KINDS, inboxAddress, normalizeAllowlistEntry } from "@/lib/api/inbox";
import { useWorkspace } from "@/lib/session/workspace-context";

export function InboxSettings() {
  const { t } = useTranslation();
  const { workspace, canManage } = useWorkspace();
  const { data: members } = useMembers(workspace?.id);
  const rotate = useRotateInboxToken(workspace?.id ?? "");
  const saveAllowlist = useUpdateAllowlist(workspace?.id ?? "");
  const [entry, setEntry] = useState("");
  const [error, setError] = useState("");

  if (!workspace) return null;
  const allowlist = Array.isArray(workspace.inboxAllowlist)
    ? (workspace.inboxAllowlist as string[])
    : [];
  // The owner's address is often the business's too: each address once (QA-0029).
  const alwaysAllowed = [
    ...new Map(
      [
        ...(members ?? []).map((member) => member.email).filter(Boolean),
        ...(workspace.email ? [workspace.email] : []),
      ].map((address) => [address.toLowerCase(), address]),
    ).values(),
  ];

  async function copy(address: string) {
    try {
      await navigator.clipboard.writeText(address);
      toast.success(t("inboxSettings.copied"));
    } catch {
      toast.error(t("inboxSettings.copyFailed"));
    }
  }

  async function add() {
    const normalized = normalizeAllowlistEntry(entry);
    if (!normalized) {
      setError(t("inboxSettings.invalidEntry"));
      return;
    }
    if (!allowlist.includes(normalized))
      await saveAllowlist.mutateAsync([...allowlist, normalized]);
    setEntry("");
    toast.success(t("inboxSettings.senderAdded"));
  }

  return (
    <div className="max-w-3xl space-y-8">
      <section className="space-y-3">
        <div>
          <h2 className="font-medium">{t("inboxSettings.addressesTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("inboxSettings.addressesHint")}</p>
        </div>
        {workspace.inboxToken ? (
          <ul className="divide-y rounded-lg border bg-card">
            {INBOX_KINDS.map((kind) => {
              const address = inboxAddress(kind, workspace.inboxToken, workspace.name);
              return (
                <li key={kind} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <KindBadge kind={kind} />
                  <span className="flex-1 text-sm text-muted-foreground sm:w-40 sm:flex-none">
                    {t(`kinds.${kind}`)}
                  </span>
                  {/* The address is what this row is for: on a phone it gets a line of its own. */}
                  <code
                    className="order-last basis-full ledger-id text-sm wrap-anywhere text-foreground sm:order-none sm:min-w-0 sm:flex-1 sm:basis-0 sm:truncate"
                    data-testid={`inbox-address-${kind.toLowerCase()}`}
                  >
                    {address}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={t("inboxSettings.copy", { address })}
                    onClick={() => void copy(address)}
                  >
                    <Copy className="size-4" />
                  </Button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-muted-foreground">{t("inboxSettings.noToken")}</p>
        )}
        <p className="text-xs text-muted-foreground">{t("inboxSettings.usage")}</p>

        {canManage && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="rotate-inbox">
                <RefreshCw /> {t("inboxSettings.rotate")}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("inboxSettings.rotateTitle")}</AlertDialogTitle>
                <AlertDialogDescription>{t("inboxSettings.rotateText")}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="confirm-rotate"
                  onClick={async () => {
                    await rotate.mutateAsync();
                    toast.success(t("inboxSettings.rotated"));
                  }}
                >
                  {t("inboxSettings.rotate")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="font-medium">{t("inboxSettings.sendersTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("inboxSettings.sendersHint")}</p>
        </div>

        <div className="space-y-1">
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {t("inboxSettings.alwaysAllowed")}
          </p>
          <ul className="flex flex-wrap gap-2" data-testid="always-allowed">
            {alwaysAllowed.map((address) => (
              <li
                key={address}
                className="max-w-full rounded-md border px-2 py-1 text-sm wrap-anywhere"
              >
                {address}
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-1">
          <p className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
            {t("inboxSettings.alsoAllowed")}
          </p>
          {allowlist.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("inboxSettings.noExtraSenders")}</p>
          ) : (
            <ul className="flex flex-wrap gap-2" data-testid="allowlist">
              {allowlist.map((allowed) => (
                <li
                  key={allowed}
                  className="flex max-w-full items-center gap-1 rounded-md border py-1 pr-1 pl-2 text-sm"
                  data-testid="allowlist-entry"
                >
                  <span className="min-w-0 wrap-anywhere">
                    {allowed.startsWith("@")
                      ? t("inboxSettings.domain", { domain: allowed.slice(1) })
                      : allowed}
                  </span>
                  {canManage && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6"
                      aria-label={t("inboxSettings.removeSender", { sender: allowed })}
                      onClick={() =>
                        saveAllowlist.mutate(allowlist.filter((item) => item !== allowed))
                      }
                    >
                      <X className="size-3.5" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {canManage && (
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-64 flex-1 space-y-1">
              <Label className="text-xs text-muted-foreground" htmlFor="allow-sender">
                {t("inboxSettings.addLabel")}
              </Label>
              <Input
                id="allow-sender"
                placeholder="rechnung@lieferant.at, stripe.com"
                value={entry}
                data-testid="allow-sender"
                onChange={(event) => {
                  setEntry(event.target.value);
                  setError("");
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") void add();
                }}
              />
              {error && (
                <p role="alert" className="text-xs text-destructive">
                  {error}
                </p>
              )}
            </div>
            <Button
              onClick={() => void add()}
              disabled={saveAllowlist.isPending}
              data-testid="add-sender"
            >
              <Plus /> {t("common.add")}
            </Button>
          </div>
        )}
      </section>
    </div>
  );
}
